import { useRef, useState } from 'react'
import { Download, Upload, Loader2, CheckCircle2, AlertTriangle, FileSpreadsheet } from 'lucide-react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { toast } from '@/components/ui/Toast'
import { extractApiError } from '@/lib/api-errors'

export interface ImportDepRow {
  rowNumber: number
  name: string
  cpf: string
  birthDate: string | null
  degreeName: string
  errors: string[]
  valid: boolean
}
export interface ImportHolderRow {
  rowNumber: number
  name: string
  cpf: string
  planName: string
  birthDate: string | null
  errors: string[]
  valid: boolean
  dependents: ImportDepRow[]
  isOrphanDependent?: boolean
}
export interface ImportPreview {
  holders: ImportHolderRow[]
  summary: {
    totalHolders: number
    validHolders: number
    invalidHolders: number
    totalDependents: number
    validDependents: number
  }
}
export interface ImportResult {
  createdHolders: number
  createdDependents: number
  skippedHolders: number
}

interface Props {
  title: string
  onClose: () => void
  onImported?: () => void
  downloadTemplate: () => Promise<Blob>
  preview: (file: File) => Promise<ImportPreview>
  confirm: (file: File) => Promise<ImportResult>
}

export function EmployeeImportModal({ title, onClose, onImported, downloadTemplate, preview, confirm }: Props) {
  const [file, setFile] = useState<File | null>(null)
  const [data, setData] = useState<ImportPreview | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [importing, setImporting] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  async function handleDownload() {
    setDownloading(true)
    try {
      const blob = await downloadTemplate()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url; a.download = 'modelo-funcionarios.xlsx'
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      toast.error({ title: 'Erro ao baixar o modelo', description: extractApiError(e).message })
    } finally { setDownloading(false) }
  }

  async function handleFile(f: File | null) {
    setFile(f); setData(null)
    if (!f) return
    setLoadingPreview(true)
    try {
      setData(await preview(f))
    } catch (e) {
      toast.error({ title: 'Não foi possível ler a planilha', description: extractApiError(e).message })
      setFile(null)
    } finally { setLoadingPreview(false) }
  }

  async function doConfirm() {
    if (!file) return
    setImporting(true)
    try {
      const res = await confirm(file)
      toast.success({
        title: 'Importação concluída',
        description: `${res.createdHolders} titular(es) e ${res.createdDependents} dependente(s) importados.`,
      })
      onImported?.()
      onClose()
    } catch (e) {
      toast.error({ title: 'Falha na importação', description: extractApiError(e).message })
    } finally { setImporting(false); setConfirmOpen(false) }
  }

  const s = data?.summary
  const canConfirm = !!s && s.validHolders > 0

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => setConfirmOpen(true)} disabled={!canConfirm} loading={importing} iconLeft={<Upload size={14} />}>
            Confirmar importação
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Passo 1 — baixar modelo */}
        <div className="rounded-md bg-brand-50/60 ring-1 ring-brand-100 p-4">
          <div className="flex items-start gap-3">
            <FileSpreadsheet size={18} className="text-brand-600 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800">1. Baixe o modelo da empresa</p>
              <p className="text-xs text-gray-500 mt-0.5">
                A planilha já vem com os planos disponíveis e os graus de parentesco para referência.
                Preencha <strong>TITULAR</strong> e <strong>DEPENDENTE</strong> na coluna “Tipo”; vincule o dependente pelo “CPF do titular”.
              </p>
              <Button size="sm" variant="secondary" className="mt-2" onClick={handleDownload} loading={downloading} iconLeft={<Download size={13} />}>
                Baixar modelo
              </Button>
            </div>
          </div>
        </div>

        {/* Passo 2 — enviar planilha */}
        <div>
          <p className="text-sm font-medium text-gray-800 mb-2">2. Envie a planilha preenchida</p>
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
            className="block w-full text-sm text-gray-600 file:mr-3 file:cursor-pointer file:rounded-md file:border-0 file:bg-brand-500 file:px-3 file:py-2 file:text-white hover:file:bg-brand-600"
          />
        </div>

        {/* Passo 3 — resumo */}
        {loadingPreview && (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-4"><Loader2 size={16} className="animate-spin" /> Lendo a planilha…</div>
        )}

        {data && s && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <SummaryCard label="Titulares válidos" value={s.validHolders} tone="green" />
              <SummaryCard label="Titulares com erro" value={s.invalidHolders} tone={s.invalidHolders ? 'rose' : 'gray'} />
              <SummaryCard label="Dependentes válidos" value={s.validDependents} tone="green" />
              <SummaryCard label="Total de linhas" value={s.totalHolders + s.totalDependents} tone="gray" />
            </div>

            <div className="max-h-72 overflow-y-auto rounded-md ring-1 ring-gray-200 divide-y divide-gray-100">
              {data.holders.map((h) => (
                <div key={h.rowNumber} className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {h.valid
                      ? <CheckCircle2 size={15} className="text-green-600 shrink-0" />
                      : <AlertTriangle size={15} className="text-rose-500 shrink-0" />}
                    <span className="shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">Linha {h.rowNumber}</span>
                    <span className="text-sm font-medium text-gray-800 break-words">{h.name || '(sem nome)'}</span>
                    {!h.isOrphanDependent && <span className="text-[11px] text-gray-400">· {h.planName || 'sem plano'}</span>}
                  </div>
                  {h.errors.length > 0 && (
                    <ul className="mt-0.5 ml-6 text-[11px] text-rose-600 list-disc list-inside">
                      {h.errors.map((e, i) => <li key={i}>{e}</li>)}
                    </ul>
                  )}
                  {h.dependents.map((d) => (
                    <div key={d.rowNumber} className="ml-6 mt-1">
                      <div className="flex items-center gap-2">
                        {d.valid
                          ? <CheckCircle2 size={13} className="text-green-500 shrink-0" />
                          : <AlertTriangle size={13} className="text-rose-400 shrink-0" />}
                        <span className="text-[13px] text-gray-600 break-words">{d.name} <span className="text-gray-400">· {d.degreeName || 'sem grau'}</span></span>
                      </div>
                      {d.errors.length > 0 && (
                        <ul className="ml-5 text-[11px] text-rose-500 list-disc list-inside">
                          {d.errors.map((e, i) => <li key={i}>{e}</li>)}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {!canConfirm && (
              <p className="text-xs text-amber-600">Nenhum titular válido para importar. Corrija a planilha e envie novamente.</p>
            )}
            {canConfirm && s.invalidHolders > 0 && (
              <p className="text-xs text-gray-500">Linhas com erro serão ignoradas; apenas os registros válidos serão importados.</p>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={doConfirm}
        loading={importing}
        title="Confirmar importação?"
        description={`Serão importados ${s?.validHolders ?? 0} titular(es) e ${s?.validDependents ?? 0} dependente(s). A importação roda em segundo plano; aguarde a conclusão.`}
        confirmLabel="Importar"
      />
    </Modal>
  )
}

function SummaryCard({ label, value, tone }: { label: string; value: number; tone: 'green' | 'rose' | 'gray' }) {
  const cls = tone === 'green' ? 'bg-green-50 text-green-700' : tone === 'rose' ? 'bg-rose-50 text-rose-700' : 'bg-gray-50 text-gray-700'
  return (
    <div className={`rounded-md px-3 py-2 ${cls}`}>
      <div className="text-lg font-semibold">{value}</div>
      <div className="text-[11px] opacity-80">{label}</div>
    </div>
  )
}
